import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Phone, Mail, Building2, Calendar, Activity, MessageSquare, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";
import { AppointmentDialog } from "./AppointmentDialog";
import { LogActivityDialog } from "./LogActivityDialog";
import { useSMSEnabled } from "@/hooks/useSMSEnabled";

type Contact = Tables<"crm_contacts">;
type Activity = Tables<"crm_activities">;

interface ContactDetailSidebarProps {
  contactId: string | null;
}

export const ContactDetailSidebar = ({ contactId }: ContactDetailSidebarProps) => {
  const [contact, setContact] = useState<Contact | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [conversations, setConversations] = useState<any[]>([]);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [logActivityOpen, setLogActivityOpen] = useState(false);
  const { toast } = useToast();
  const { data: smsEnabled } = useSMSEnabled();
  const navigate = useNavigate();

  useEffect(() => {
    if (contactId) {
      fetchContact();
      fetchActivities();
      fetchAppointments();
      fetchConversations();
    }
  }, [contactId]);

  const fetchContact = async () => {
    if (!contactId) return;

    const { data, error } = await supabase
      .from('crm_contacts')
      .select('*')
      .eq('id', contactId)
      .single();

    if (error) {
      console.error('Error fetching contact:', error);
    } else {
      setContact(data);
    }
  };

  const fetchActivities = async () => {
    if (!contactId) return;

    const { data, error } = await supabase
      .from('crm_activities')
      .select('*')
      .eq('contact_id', contactId)
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error fetching activities:', error);
    } else {
      setActivities(data || []);
    }
  };

  const fetchAppointments = async () => {
    if (!contactId) return;

    const { data, error } = await supabase
      .from('appointments')
      .select('*')
      .eq('crm_contact_id', contactId)
      .order('start_time', { ascending: false })
      .limit(5);

    if (error) {
      console.error('Error fetching appointments:', error);
    } else {
      setAppointments(data || []);
    }
  };

  const fetchConversations = async () => {
    if (!contactId) return;

    const { data, error } = await supabase
      .from('conversations')
      .select(`
        id,
        status,
        created_at,
        last_message_at,
        messages (count)
      `)
      .eq('crm_contact_id', contactId)
      .order('last_message_at', { ascending: false });

    if (error) {
      console.error('Error fetching conversations:', error);
    } else {
      setConversations(data || []);
    }
  };

  const handleAddNote = async () => {
    if (!contactId || !newNote.trim()) return;

    setLoading(true);
    const { error } = await supabase
      .from('crm_activities')
      .insert({
        contact_id: contactId,
        activity_type: 'note',
        description: newNote,
        created_by: (await supabase.auth.getUser()).data.user?.id
      });

    if (error) {
      toast({
        title: "Error",
        description: "Failed to add note",
        variant: "destructive",
      });
    } else {
      setNewNote("");
      fetchActivities();
      toast({
        title: "Success",
        description: "Note added successfully",
      });
    }
    setLoading(false);
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'lead': return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'prospect': return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
      case 'customer': return 'bg-green-500/10 text-green-600 border-green-500/20';
      case 'inactive': return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
      default: return '';
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'sms': return '💬';
      case 'email': return '📧';
      case 'phone_call': return '📞';
      case 'meeting': return '🤝';
      case 'note': return '📝';
      case 'status_change': return '🔄';
      case 'stage_change': return '📊';
      default: return '•';
    }
  };

  const handleEmailClick = () => {
    if (contact?.email) {
      window.location.href = `mailto:${contact.email}`;
    }
  };

  const handlePhoneClick = () => {
    if (contact?.phone_number) {
      window.location.href = `tel:${contact.phone_number}`;
    }
  };

  const handleSendMessage = async () => {
    if (!contactId) return;

    try {
      const { data, error } = await supabase.functions.invoke('start-conversation', {
        body: { crm_contact_id: contactId }
      });

      if (error) throw error;

      navigate(`/dashboard?conversation=${data.conversation_id}`);
      
      toast({
        title: "Conversation started",
        description: "Ready to send a message",
      });
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast({
        title: "Error",
        description: "Failed to start conversation",
        variant: "destructive",
      });
    }
  };

  if (!contactId || !contact) {
    return (
      <Card className="h-full">
        <CardContent className="flex items-center justify-center h-full text-muted-foreground">
          Select a conversation to view contact details
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full overflow-auto">
      <CardHeader>
        <CardTitle className="text-lg">Contact Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-xl">{contact.name || 'Unknown'}</h3>
            <div className="flex gap-2">
              {contact.email && (
                <Button size="sm" variant="outline" onClick={handleEmailClick} title="Send email">
                  <Mail className="h-4 w-4" />
                </Button>
              )}
              {contact.phone_number && (
                <Button size="sm" variant="outline" onClick={handlePhoneClick} title="Call">
                  <Phone className="h-4 w-4" />
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setLogActivityOpen(true)} title="Log activity">
                <FileText className="h-4 w-4" />
              </Button>
              {smsEnabled && (
                <Button size="sm" onClick={handleSendMessage} className="gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Message
                </Button>
              )}
            </div>
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Phone className="h-4 w-4" />
              {contact.phone_number}
            </div>
            
            {contact.email && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-4 w-4" />
                {contact.email}
              </div>
            )}
            
            {contact.company && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Building2 className="h-4 w-4" />
                {contact.company}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Badge variant="outline" className={cn("text-xs", getStageColor(contact.lifecycle_stage || 'lead'))}>
              {contact.lifecycle_stage}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {contact.priority} priority
            </Badge>
          </div>
        </div>

        {contact.notes && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Notes</h4>
            <p className="text-sm text-muted-foreground">{contact.notes}</p>
          </div>
        )}

        <div className="space-y-2">
          <h4 className="font-medium text-sm flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Conversations
          </h4>
          <div className="space-y-2">
            {conversations.length === 0 ? (
              <p className="text-sm text-muted-foreground">No conversations yet</p>
            ) : (
              conversations.map((conv) => (
                <div 
                  key={conv.id} 
                  className="text-sm border rounded-lg p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/dashboard?conversation=${conv.id}`)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <Badge variant="outline" className="text-xs">
                      {conv.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(conv.last_message_at), 'MMM d')}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {conv.messages?.[0]?.count || 0} messages
                  </p>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="font-medium text-sm flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Activity Timeline
          </h4>
          <div className="space-y-2">
            {activities.length === 0 ? (
              <p className="text-sm text-muted-foreground">No activity yet</p>
            ) : (
              activities.map((activity) => {
                const metadata = activity.metadata as any;
                return (
                  <div key={activity.id} className="text-sm border-l-2 border-border pl-3 py-2">
                    <div className="flex items-start gap-2">
                      <span>{getActivityIcon(activity.activity_type)}</span>
                      <div className="flex-1">
                        {metadata?.subject && (
                          <p className="font-medium text-sm mb-1">{metadata.subject}</p>
                        )}
                        <p className="text-muted-foreground">{activity.description}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(activity.created_at), 'MMM d, h:mm a')}
                          {activity.activity_type === 'phone_call' && metadata?.duration && (
                            <span className="text-xs">• {metadata.duration} min</span>
                          )}
                          {metadata?.direction && (
                            <span className="text-xs capitalize">• {metadata.direction}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="font-medium text-sm flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Appointments
            </span>
            <Button size="sm" variant="outline" onClick={() => setAppointmentDialogOpen(true)}>
              Schedule
            </Button>
          </h4>
          <div className="space-y-2">
            {appointments.length === 0 ? (
              <p className="text-sm text-muted-foreground">No appointments scheduled</p>
            ) : (
              appointments.map((apt) => (
                <div key={apt.id} className="text-sm border-l-2 border-primary pl-3 py-2">
                  <p className="font-medium">{apt.title}</p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <Calendar className="h-3 w-3" />
                    {format(parseISO(apt.start_time), 'MMM d, h:mm a')}
                  </div>
                  <Badge variant="outline" className="text-xs mt-1">{apt.status}</Badge>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="font-medium text-sm">Add Note</h4>
          <Textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Add a note about this contact..."
            rows={3}
          />
          <Button
            onClick={handleAddNote}
            disabled={loading || !newNote.trim()}
            className="w-full"
            size="sm"
          >
            {loading ? 'Adding...' : 'Add Note'}
          </Button>
        </div>
      </CardContent>
      
      <AppointmentDialog
        open={appointmentDialogOpen}
        onOpenChange={setAppointmentDialogOpen}
        prefilledContactId={contactId || undefined}
        onSuccess={() => {
          fetchAppointments();
          fetchActivities();
        }}
      />

      <LogActivityDialog
        open={logActivityOpen}
        onOpenChange={setLogActivityOpen}
        contactId={contactId || ''}
        contactName={contact?.name || ''}
      />
    </Card>
  );
};
